// CSR-856: Insurance Company detail page redesigned to match the
// lodestar /insurers/:id prototype.
//
// Layout:
//   * Back link to /companies
//   * Dark gradient header card — Shield tile + name + category pill +
//     meta row (NAIC, Parent link, City+State, Created date) + Edit
//     button on the right
//   * Info cards row — Commission Rates (amber tinted), Contact &
//     Location, Quick Stats (Total Policies). Commission Rates is
//     hidden for parent_company per PRD; that variant uses a 2-card
//     grid instead.
//   * Policies card — header bar with title + count, then table or
//     empty / loading / error state.
//   * Child Companies card — parent_company variant only; clickable
//     rows that navigate to the child's detail page.
//
// CSR-824 introduced the Carrier/MGA variant; CSR-825 was meant to add
// the Parent variant but was superseded by this redesign. The parent
// branch now renders the full layout (sans Commission Rates) plus the
// Child Companies card.

import { useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { format } from "date-fns";
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  Globe,
  Loader2,
  MapPin,
  Pencil,
  Shield,
} from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { formatPolicyStatusLabel } from "@/components/insured/policies/policy-status";
import {
  useInsuranceCompanies,
  useInsuranceCompany,
  useInsuranceCompanyChildren,
  useInsuranceCompanyPolicies,
  useInsuranceCompanyStats,
  type InsuranceCompanyChild,
} from "@/lib/api";
import { CommissionRatesSection } from "@/components/companies/CommissionRatesSection";
import { ContactsSection } from "@/components/companies/ContactsSection";
import { DetailSectionHeader } from "@/components/companies/DetailSectionHeader";
import { InsuranceCompanyFormDialog } from "@/components/companies/InsuranceCompanyFormDialog";
import type {
  InsuranceCompany,
  InsuranceCompanyCategory,
  Policy,
} from "@shared/schema";

const POLICIES_PAGE_SIZE = 25;

const CATEGORY_LABEL: Record<InsuranceCompanyCategory, string> = {
  parent_company: "Parent Company",
  insurance_carrier: "Insurance Carrier",
  mga: "MGA",
};

// Category pill colors — the dark-header version uses the same hues as
// the list cards but with white border tweaks for legibility over the
// gradient. The Child Companies row reuses the light variant.
const CATEGORY_BADGE_LIGHT: Record<InsuranceCompanyCategory, string> = {
  parent_company: "bg-purple-500/10 text-purple-700 border-purple-200",
  insurance_carrier: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  mga: "bg-blue-500/10 text-blue-700 border-blue-200",
};

const CATEGORY_BADGE_ON_DARK: Record<InsuranceCompanyCategory, string> = {
  parent_company: "bg-purple-400/20 text-purple-100 border-purple-300/40",
  insurance_carrier: "bg-emerald-400/20 text-emerald-100 border-emerald-300/40",
  mga: "bg-blue-400/20 text-blue-100 border-blue-300/40",
};

function formatCommission(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)}%`;
}

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

function formatPremium(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return currencyFormatter.format(num);
}

// CSR-880 — policy status pill styling. Maps the raw status string
// (case-insensitive) to a tailwind class set. "Active" / "Renewed" /
// "Reinstated" read as live coverage and get the emerald treatment;
// terminal / pending-terminal states get the muted neutral pill.
function policyStatusClasses(status: string | null | undefined): string {
  const s = (status ?? "").toLowerCase().trim();
  if (
    s === "active" ||
    s === "renewing" ||
    s === "renewed" ||
    s === "reinstated"
  ) {
    return "bg-emerald-500/10 text-emerald-700 border-emerald-200";
  }
  // Cancelled, pending cancel, non-renewal, expired, lost → muted.
  // CSR-1042: "renewal offer" intentionally falls into this branch — it is not
  // active coverage (the carrier has offered renewal but it has not been bound).
  return "bg-muted text-muted-foreground border-border";
}

function ComposedAddress({ company }: { company: InsuranceCompany }) {
  const parts = [
    company.street,
    [company.city, company.state].filter(Boolean).join(", "),
    company.zip,
  ].filter((p): p is string => Boolean(p && p.trim().length > 0));
  if (parts.length === 0) return <span className="text-muted-foreground">—</span>;
  return <span>{parts.join(" • ")}</span>;
}

function HeaderCard({
  company,
  parentName,
  parentId,
  onEdit,
}: {
  company: InsuranceCompany;
  parentName: string | null;
  parentId: string | null;
  onEdit: () => void;
}) {
  return (
    <Card
      className="p-0 border-0 shadow-md overflow-hidden"
      data-testid="ic-detail-header"
    >
      <div className="bg-gradient-to-r from-[#1B2340] via-[#243054] to-[#2D3A60] px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white shrink-0">
              <Shield className="w-6 h-6" />
            </div>
            <div className="flex flex-col gap-2 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1
                  className="text-2xl font-extrabold font-heading text-white truncate"
                  title={company.name}
                  data-testid="ic-detail-name"
                >
                  {company.name}
                </h1>
                <span
                  className={cn(
                    "px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap",
                    CATEGORY_BADGE_ON_DARK[company.category],
                  )}
                  data-testid="ic-detail-category"
                >
                  {CATEGORY_LABEL[company.category]}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/80">
                {company.naic && (
                  <span data-testid="ic-detail-naic">NAIC: {company.naic}</span>
                )}
                {parentId && parentName && (
                  <span
                    className="flex items-center gap-1"
                    data-testid="ic-detail-parent"
                  >
                    Parent:{" "}
                    <Link
                      href={`/companies/insurers/${parentId}`}
                      className="underline hover:text-white"
                      data-testid="link-parent"
                    >
                      {parentName}
                    </Link>
                  </span>
                )}
                {company.city && company.state && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {company.city}, {company.state}
                  </span>
                )}
                {company.createdAt && (
                  <span
                    className="flex items-center gap-1"
                    data-testid="ic-detail-created"
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    Created {format(new Date(company.createdAt), "MMM d, yyyy")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            className="shrink-0 rounded-xl font-semibold bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            onClick={onEdit}
            data-testid="button-ic-edit"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>
    </Card>
  );
}

function CommissionRatesCard({ company }: { company: InsuranceCompany }) {
  return (
    <Card data-testid="card-commission-rates">
      <CardContent className="p-5 flex flex-col gap-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Commission Rates
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-amber-500/5 rounded-xl border border-amber-200/50">
            <p
              className="text-2xl font-bold text-amber-600"
              data-testid="commission-new"
            >
              {formatCommission(company.newCommissionPct)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">New Business</p>
          </div>
          <div className="text-center p-3 bg-amber-500/5 rounded-xl border border-amber-200/50">
            <p
              className="text-2xl font-bold text-amber-600"
              data-testid="commission-renewal"
            >
              {formatCommission(company.renewalCommissionPct)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Renewal</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ContactLocationCard({ company }: { company: InsuranceCompany }) {
  const hasWebsite = Boolean(company.website);
  const hasAddress = Boolean(
    company.street || company.city || company.state || company.zip,
  );
  return (
    <Card data-testid="card-contact-location">
      <CardContent className="p-5 flex flex-col gap-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Contact &amp; Location
        </h3>
        <div className="flex flex-col gap-2 text-sm">
          {hasWebsite ? (
            <p className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
              <a
                href={company.website ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline truncate"
                data-testid="link-website"
                title={company.website ?? undefined}
              >
                {company.website}
              </a>
            </p>
          ) : (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Globe className="w-4 h-4 shrink-0" />
              <span data-testid="text-website-empty">—</span>
            </p>
          )}
          {hasAddress && (
            <p
              className="flex items-start gap-2 text-muted-foreground"
              data-testid="text-address"
            >
              <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
              <ComposedAddress company={company} />
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// CSR-885 — Quick Stats now shows ACTIVE policies aggregated across the
// parent + all descendants (for parent_company rows). The previous
// implementation piggybacked on the paginated policies query and
// (a) included terminal statuses and (b) ignored child companies.
function QuickStatsCard({ activePolicies }: { activePolicies: number | null }) {
  return (
    <Card data-testid="card-quick-stats">
      <CardContent className="p-5 flex flex-col gap-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Quick Stats
        </h3>
        <div className="text-center p-3 bg-muted/30 rounded-xl">
          <p
            className="text-2xl font-bold text-primary"
            data-testid="stat-active-policies"
          >
            {activePolicies === null ? "—" : activePolicies}
          </p>
          <p className="text-xs text-muted-foreground mt-1">Active Policies</p>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoCardsRow({
  company,
  activePolicies,
}: {
  company: InsuranceCompany;
  activePolicies: number | null;
}) {
  const isParent = company.category === "parent_company";
  // Parents skip the Commission Rates card per PRD; the row collapses
  // to a 2-col grid so the remaining cards stretch evenly.
  return (
    <div
      className={cn(
        "grid gap-4 grid-cols-1",
        isParent ? "md:grid-cols-2" : "md:grid-cols-3",
      )}
    >
      {!isParent && <CommissionRatesCard company={company} />}
      <ContactLocationCard company={company} />
      <QuickStatsCard activePolicies={activePolicies} />
    </div>
  );
}

function PoliciesSection({ companyId }: { companyId: string }) {
  const [, navigate] = useLocation();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useInsuranceCompanyPolicies(
    companyId,
    page,
    POLICIES_PAGE_SIZE,
  );
  const total = data?.total ?? 0;
  const totalPages = total === 0 ? 1 : Math.ceil(total / POLICIES_PAGE_SIZE);

  return (
    <section className="flex flex-col gap-3" data-testid="section-policies">
      <Card className="overflow-hidden p-0">
        <DetailSectionHeader
          icon={<FileText className="w-5 h-5 text-primary" />}
          title="Policies"
          count={total > 0 ? total : undefined}
          testid="policies-total"
        />
        {error ? (
          <div className="p-6 text-sm text-destructive" data-testid="policies-error">
            Failed to load policies for this insurer.
          </div>
        ) : isLoading || !data ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading policies…
          </div>
        ) : data.data.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground" data-testid="policies-empty">
            No policies for this insurer.
          </div>
        ) : (
          <Table className="[&_th]:h-auto [&_th]:px-6 [&_th]:py-3 [&_td]:px-6 [&_td]:py-4">
            <TableHeader>
              <TableRow>
                <TableHead>Policy Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Premium</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Expiration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((p: Policy) => (
                <TableRow
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/policies/${p.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/policies/${p.id}`);
                    }
                  }}
                  className="cursor-pointer hover-elevate"
                  data-testid={`policy-row-${p.id}`}
                >
                  <TableCell className="font-medium" data-testid="policy-cell-number">
                    {p.policyNumber}
                  </TableCell>
                  <TableCell data-testid="policy-cell-type">{p.type ?? "—"}</TableCell>
                  <TableCell data-testid="policy-cell-status">
                    <span
                      className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap",
                        policyStatusClasses(p.status),
                      )}
                    >
                      {formatPolicyStatusLabel(p.status)}
                    </span>
                  </TableCell>
                  <TableCell
                    className="font-semibold text-amber-600"
                    data-testid="policy-cell-premium"
                  >
                    {formatPremium(p.premium)}
                  </TableCell>
                  <TableCell data-testid="policy-cell-effective">
                    {formatDate(p.effectiveDate)}
                  </TableCell>
                  <TableCell data-testid="policy-cell-expiration">
                    {formatDate(p.expirationDate)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {data && data.data.length > 0 && totalPages > 1 && (
        <div
          className="flex items-center justify-end gap-2 text-sm"
          data-testid="policies-pagination"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="policies-prev"
          >
            Previous
          </Button>
          <span className="text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="policies-next"
          >
            Next
          </Button>
        </div>
      )}
    </section>
  );
}

function ChildCompanyRow({ child }: { child: InsuranceCompanyChild }) {
  const [, navigate] = useLocation();
  return (
    <button
      type="button"
      onClick={() => navigate(`/companies/insurers/${child.id}`)}
      className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors text-left"
      data-testid={`child-company-row-${child.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={cn(
            "px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap",
            CATEGORY_BADGE_LIGHT[child.category as InsuranceCompanyCategory],
          )}
        >
          {CATEGORY_LABEL[child.category as InsuranceCompanyCategory]}
        </span>
        <span className="font-medium text-foreground truncate" title={child.name}>
          {child.name}
        </span>
        {child.naic && (
          <span className="text-xs text-muted-foreground shrink-0">
            NAIC: {child.naic}
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground shrink-0">
        {child.policyCount} {child.policyCount === 1 ? "policy" : "policies"}
      </span>
    </button>
  );
}

function ChildCompaniesSection({ companyId }: { companyId: string }) {
  const { data, isLoading, error } = useInsuranceCompanyChildren(companyId);
  const count = data?.length ?? 0;

  return (
    <section className="flex flex-col gap-3" data-testid="section-child-companies">
      <Card className="overflow-hidden p-0">
        <DetailSectionHeader
          icon={<Building2 className="w-5 h-5 text-primary" />}
          title="Child Companies"
          count={count > 0 ? count : undefined}
          testid="child-companies-total"
        />
        {error ? (
          <div
            className="p-6 text-sm text-destructive"
            data-testid="child-companies-error"
          >
            Failed to load child companies.
          </div>
        ) : isLoading || !data ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading children…
          </div>
        ) : data.length === 0 ? (
          <p
            className="p-6 text-sm text-muted-foreground italic"
            data-testid="child-companies-empty"
          >
            No child companies linked to this parent yet.
          </p>
        ) : (
          <div className="divide-y divide-border/50">
            {data.map((child) => (
              <ChildCompanyRow key={child.id} child={child} />
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}

function NotFoundCard() {
  return (
    <Card className="border-border shadow-none">
      <CardContent className="py-16 text-center flex flex-col items-center gap-3">
        <h1 className="text-xl font-bold font-heading text-foreground">
          Insurance company not found
        </h1>
        <Link href="/companies">
          <Button variant="secondary">Return to Companies</Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export default function InsuranceCompanyDetail() {
  const [, params] = useRoute("/companies/insurers/:id");
  const id = params?.id ?? null;
  const { data: company, isLoading, error } = useInsuranceCompany(id);

  // Resolve parent name from the cached list. CSR-822 already
  // primes this query for the Companies page; if the user lands here
  // via direct URL it just shows the id-less label until the list loads.
  const { data: listData } = useInsuranceCompanies();
  const parentName = useMemo(() => {
    if (!company?.parentCompanyId || !listData) return null;
    const found = listData.parentCompanies.find(
      (p) => p.id === company.parentCompanyId,
    );
    return found?.name ?? null;
  }, [company, listData]);

  const [editOpen, setEditOpen] = useState(false);

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <div>
          <Link
            href="/companies"
            className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
            data-testid="link-back-to-companies"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Companies
          </Link>
        </div>

        {error ? (
          <NotFoundCard />
        ) : isLoading || !company ? (
          <div className="flex flex-col gap-4" data-testid="ic-detail-loading">
            <Skeleton className="h-32 w-full rounded-md" />
            <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
              <Skeleton className="h-28 rounded-md" />
              <Skeleton className="h-28 rounded-md" />
              <Skeleton className="h-28 rounded-md" />
            </div>
            <Skeleton className="h-64 w-full rounded-md" />
          </div>
        ) : (
          <>
            <HeaderCard
              company={company}
              parentName={parentName}
              parentId={company.parentCompanyId}
              onEdit={() => setEditOpen(true)}
            />
            <InfoCardsRowWithPolicyCount company={company} />
            {company.category !== "parent_company" && (
              <CommissionRatesSection companyId={company.id} />
            )}
            <PoliciesSection companyId={company.id} />
            {company.category === "parent_company" && (
              <ChildCompaniesSection companyId={company.id} />
            )}
            <ContactsSection companyId={company.id} />
            <InsuranceCompanyFormDialog
              open={editOpen}
              onOpenChange={setEditOpen}
              mode="edit"
              company={company}
            />
          </>
        )}
      </div>
    </Layout>
  );
}

// Small wrapper so the info cards row can read the active-policy count
// (CSR-885 — aggregated across descendants for parent rows, status-
// filtered everywhere) without re-querying inside the card.
function InfoCardsRowWithPolicyCount({ company }: { company: InsuranceCompany }) {
  const { data } = useInsuranceCompanyStats(company.id);
  return (
    <InfoCardsRow
      company={company}
      activePolicies={data?.activePolicyCount ?? null}
    />
  );
}
